/**
 * Settings feature module (W2-13 settings-keymap; DESIGN.md §4.20, §5.15, §8; WORKPACKAGES Appendix B: `settings.*`,
 * `keymap.*`).
 *
 * Provides:
 *  - routes `#/settings` and `#/settings/:section` (section id, row id or setting path);
 *  - `DIALOG.keymapRecorder` (modal; the component renders its own Dialog frame);
 *  - the `:` palette provider (settings rows and sections, by title, description and keywords);
 *  - commands: `settings.open` (Ctrl+, / macOS Cmd+;), `settings.toggleLevel`, `settings.setLanguage`,
 *    `settings.setTheme`, `settings.setHintBar`, `settings.resetAll`, `settings.applyPresetConventions`,
 *    `keymap.open`, `keymap.setPreset`, `keymap.toggleAltFree`, `keymap.record`, `keymap.import`, `keymap.export`,
 *    `keymap.importRpe`, `keymap.resetAll`;
 *  - help topics about settings and shortcuts;
 *  - `init`: keeps the i18n runtime on settings.general.locale, whoever changes it (first-run question, another
 *    tab, this page), so a language switch re-renders the whole app.
 */

import { DIALOG } from '../../app/dialogs/types';
import type {
  AppServices,
  CommandContext,
  CommandDef,
  FeatureModule,
  HelpTopicDef,
  KeyBinding,
  PaletteItem,
  PaletteProvider,
} from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { Locale } from '../../core/util/types';
import { downloadBlob, pickFile, readText } from './files';
import { KeyRecorderDialog } from './KeyRecorderDialog';
import {
  addBinding,
  bindingTemplate,
  isUserBinding,
  matchesTerms,
  mergeImportedBindings,
  replaceBinding,
  searchTerms,
  userBinding,
} from './keymapModel';
import { applyConventions, switchPreset } from './rowExtras';
import { BLOCKS, PAGE_SECTIONS, rowSearchTexts, type PageSectionId } from './settingsCatalog';
import { m } from './settings.messages';
import { settingsRows, SettingsRoute } from './SettingsRoute';

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

function argOf<T extends string>(args: unknown, key: string, allowed: readonly T[]): T | undefined {
  if (typeof args === 'string') return (allowed as readonly string[]).includes(args) ? (args as T) : undefined;
  if (typeof args !== 'object' || args === null) return undefined;
  const v = (args as Readonly<Record<string, unknown>>)[key];
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function stringArg(args: unknown, key: string): string | undefined {
  if (typeof args !== 'object' || args === null) return undefined;
  const v = (args as Readonly<Record<string, unknown>>)[key];
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function setLocale(ctx: Pick<AppServices, 'settings' | 'i18n'>, locale: Locale): void {
  ctx.settings.set('general', 'locale', locale);
  if (ctx.i18n.locale !== locale) ctx.i18n.setLocale(locale);
}

const LOCALES: readonly Locale[] = ['en', 'zh-CN'];
const THEMES: ReadonlyArray<UserSettings['general']['theme']> = ['dark', 'light', 'highContrast'];
const HINT_BARS: ReadonlyArray<UserSettings['general']['hintBar']> = ['sentences', 'keys', 'off'];
const PRESETS = ['default', 'rpe'] as const;

async function confirmResetAll(ctx: CommandContext): Promise<void> {
  const ok = await ctx.ui.confirm({
    title: { key: m.resetAllTitle },
    body: { key: m.resetAllBody },
    confirm: { key: m.resetAllConfirm },
    danger: true,
  });
  if (!ok) return;
  ctx.settings.reset();
  const locale = ctx.settings.value.general.locale;
  if (ctx.i18n.locale !== locale) ctx.i18n.setLocale(locale);
  ctx.ui.toast({ key: m.resetAllDone }, { kind: 'success' });
}

/** Records a shortcut through `DIALOG.keymapRecorder` and adds it (or replaces `current`). */
async function recordShortcut(ctx: CommandContext, command: string, current: string | undefined): Promise<void> {
  const result = await ctx.ui.openDialog(DIALOG.keymapRecorder, {
    command,
    ...(current === undefined ? {} : { current }),
  });
  if (result === null) return;
  const tmpl = bindingTemplate(ctx.keymap.bindings(), command, current);
  const overrides = ctx.settings.value.keyboard.overrides;
  const next =
    tmpl.replacing === null
      ? addBinding(overrides, userBinding({ chord: result.chord, command, args: tmpl.args, when: tmpl.when }))
      : replaceBinding(
          overrides,
          { binding: tmpl.replacing, source: isUserBinding(tmpl.replacing, overrides) ? 'user' : 'preset' },
          result.chord,
        );
  ctx.keymap.setOverrides(next);
  ctx.ui.toast({ key: m.recordedToast, params: { chord: ctx.keymap.label(result.chord) } }, { kind: 'success' });
}

/**
 * Picks an RPE Hotkey.txt and merges its keys like the keymap editor does (they replace the keys of the commands
 * they name); the result is a toast, with a link to the editor when some RPE functions have no counterpart.
 */
async function importRpeHotkeys(ctx: CommandContext): Promise<void> {
  if (ctx.keymap.importRpeHotkeys === undefined) {
    ctx.ui.toast({ key: m.rpeImportUnavailable }, { kind: 'error' });
    return;
  }
  const file = await pickFile('.txt,text/plain');
  if (file === null) return;
  let content: string;
  try {
    content = await readText(file);
  } catch {
    ctx.ui.toast({ key: m.importUnreadable }, { kind: 'error' });
    return;
  }
  const result = ctx.keymap.importRpeHotkeys(content);
  const openEditor = { label: { key: m.openKeymap }, command: 'keymap.open' } as const;
  if (result.bindings.length === 0) {
    ctx.ui.toast({ key: m.rpeImportNone }, { kind: 'error', action: openEditor });
    return;
  }
  ctx.keymap.setOverrides(
    mergeImportedBindings(ctx.settings.value.keyboard.overrides, ctx.keymap.bindings(), result.bindings),
  );
  if (result.unmapped.length === 0) {
    ctx.ui.toast({ key: m.rpeImported, params: { count: result.bindings.length } }, { kind: 'success' });
  } else {
    ctx.ui.toast(
      {
        key: m.rpeImportedPartialToast,
        params: { count: result.bindings.length, unmapped: result.unmapped.length },
      },
      { kind: 'warning', action: openEditor },
    );
  }
}

export const SETTINGS_COMMANDS: readonly CommandDef[] = [
  {
    id: 'settings.open',
    title: m.cmdOpen,
    category: 'File',
    icon: 'Settings',
    level: 'guided',
    aliases: ['preferences', 'options', 'configuration', 'settings', '设置', '偏好设置', '选项'],
    hint: 1,
    menus: [{ menu: 'app.project', group: 'app', order: 900 }],
    run(ctx, args) {
      ctx.nav.goSettings(stringArg(args, 'section'));
    },
  },
  {
    id: 'keymap.open',
    title: m.cmdKeymapOpen,
    category: 'Help',
    icon: 'Keyboard',
    level: 'guided',
    aliases: ['keymap', 'hotkeys', 'shortcuts', 'key bindings', 'keybindings', 'rebind', '快捷键', '热键', 'Hotkey'],
    menus: [{ menu: 'app.help', group: 'keys', order: 20 }],
    run(ctx) {
      ctx.nav.goSettings('keyboard');
    },
  },
  {
    id: 'settings.toggleLevel',
    title: m.cmdToggleLevel,
    category: 'View',
    icon: 'Gauge',
    level: 'guided',
    aliases: ['guided', 'full', 'experience level', 'beginner', 'advanced', '模式'],
    checked: (ctx) => ctx.settings.value.general.level === 'full',
    run(ctx) {
      ctx.settings.set('general', 'level', ctx.settings.value.general.level === 'full' ? 'guided' : 'full');
    },
  },
  {
    id: 'settings.setLanguage',
    title: m.cmdSetLanguage,
    category: 'View',
    icon: 'Languages',
    level: 'guided',
    aliases: ['language', 'locale', 'english', 'chinese', '中文', '语言', '简体中文'],
    args: {
      kind: 'choice',
      label: { key: m.locale },
      options: [
        { value: 'en', label: { key: m.languageEnglish } },
        { value: 'zh-CN', label: { key: m.languageChinese } },
      ],
    },
    run(ctx, args) {
      const locale = argOf(args, 'locale', LOCALES);
      if (locale !== undefined) setLocale(ctx, locale);
    },
  },
  {
    id: 'settings.setTheme',
    title: m.cmdSetTheme,
    category: 'View',
    icon: 'SunMoon',
    level: 'guided',
    aliases: ['theme', 'dark', 'light', 'high contrast', 'colours', 'colors', '主题'],
    args: {
      kind: 'choice',
      label: { key: m.theme },
      options: [
        { value: 'dark', label: { key: m.themeDark } },
        { value: 'light', label: { key: m.themeLight } },
        { value: 'highContrast', label: { key: m.themeHighContrast } },
      ],
    },
    run(ctx, args) {
      const theme = argOf(args, 'theme', THEMES);
      if (theme !== undefined) ctx.settings.set('general', 'theme', theme);
    },
  },
  {
    id: 'settings.setHintBar',
    title: m.cmdSetHintBar,
    category: 'View',
    icon: 'MessageSquareText',
    level: 'guided',
    aliases: ['hint bar', 'hints', 'status bar', '提示'],
    args: {
      kind: 'choice',
      label: { key: m.hintBar },
      options: [
        { value: 'sentences', label: { key: m.hintBarSentences } },
        { value: 'keys', label: { key: m.hintBarKeys } },
        { value: 'off', label: { key: m.hintBarOff } },
      ],
    },
    run(ctx, args) {
      const v = argOf(args, 'hintBar', HINT_BARS);
      if (v !== undefined) ctx.settings.set('general', 'hintBar', v);
    },
  },
  {
    id: 'settings.resetAll',
    title: m.cmdResetAll,
    category: 'File',
    icon: 'RotateCcw',
    level: 'full',
    aliases: ['factory reset', 'restore defaults'],
    async run(ctx) {
      await confirmResetAll(ctx);
    },
  },
  {
    id: 'settings.applyPresetConventions',
    title: m.cmdApplyConventions,
    category: 'Edit',
    icon: 'Keyboard',
    level: 'guided',
    aliases: ['rpe conventions', 'paste immediately', 'beat syntax'],
    args: {
      kind: 'choice',
      label: { key: m.preset },
      options: [
        { value: 'default', label: { key: m.presetDefault } },
        { value: 'rpe', label: { key: m.presetRpe } },
      ],
    },
    run(ctx, args) {
      const preset = argOf(args, 'preset', PRESETS) ?? ctx.settings.value.keyboard.preset;
      applyConventions(ctx, preset);
      ctx.ui.toast({ key: m.conventionsApplied }, { kind: 'success' });
    },
  },
  {
    id: 'keymap.setPreset',
    title: m.cmdSetPreset,
    category: 'Help',
    icon: 'Keyboard',
    level: 'guided',
    aliases: ['rpe keys', 'default keys', 'keyboard preset', 'Re:PhiEdit'],
    args: {
      kind: 'choice',
      label: { key: m.preset },
      options: [
        { value: 'default', label: { key: m.presetDefault } },
        { value: 'rpe', label: { key: m.presetRpe } },
      ],
    },
    run(ctx, args) {
      const preset = argOf(args, 'preset', PRESETS);
      if (preset !== undefined) switchPreset(ctx, preset);
    },
  },
  {
    id: 'keymap.toggleAltFree',
    title: m.cmdToggleAltFree,
    category: 'Help',
    icon: 'Keyboard',
    level: 'full',
    aliases: ['alt free', 'no alt', 'window manager'],
    checked: (ctx) => ctx.keymap.altFree,
    run(ctx) {
      const next = !ctx.keymap.altFree;
      ctx.keymap.setAltFree(next);
      if (ctx.settings.value.keyboard.altFree !== next) ctx.settings.set('keyboard', 'altFree', next);
    },
  },
  {
    id: 'keymap.record',
    title: m.cmdRecord,
    category: 'Help',
    icon: 'Keyboard',
    level: 'guided',
    aliases: ['rebind', 'change shortcut', 'assign key'],
    async run(ctx, args) {
      const command = stringArg(args, 'command');
      if (command === undefined || ctx.registry.command(command) === undefined) {
        // From the palette: there is no command to record for yet; the keymap editor lists them all.
        ctx.nav.goSettings('keymap');
        return;
      }
      await recordShortcut(ctx, command, stringArg(args, 'current'));
    },
  },
  {
    id: 'keymap.import',
    title: m.cmdImport,
    category: 'File',
    icon: 'FileUp',
    level: 'full',
    aliases: ['import shortcuts', 'load keymap', 'keymap json'],
    async run(ctx) {
      const file = await pickFile('.json,application/json');
      if (file === null) return;
      let content: string;
      try {
        content = await readText(file);
      } catch {
        ctx.ui.toast({ key: m.importUnreadable }, { kind: 'error' });
        return;
      }
      const result = ctx.keymap.importJson(content);
      if (result.ok) ctx.ui.toast({ key: m.importDoneTitle }, { kind: 'success' });
      else {
        ctx.ui.toast(
          { key: m.importFailedToast, params: { error: result.errors[0] ?? '' } },
          { kind: 'error', action: { label: { key: m.openKeymap }, command: 'keymap.open' } },
        );
      }
    },
  },
  {
    id: 'keymap.export',
    title: m.cmdExport,
    category: 'File',
    icon: 'FileDown',
    level: 'full',
    aliases: ['export shortcuts', 'save keymap', 'keymap json', 'backup shortcuts'],
    run(ctx) {
      downloadBlob(new Blob([ctx.keymap.exportJson()], { type: 'application/json' }), 'phiedit-keymap.json');
      ctx.ui.toast({ key: m.exportDone }, { kind: 'success' });
    },
  },
  {
    id: 'keymap.importRpe',
    title: m.cmdImportRpe,
    category: 'File',
    icon: 'FileInput',
    level: 'full',
    aliases: ['Hotkey.txt', 'rpe hotkeys', 'import rpe shortcuts'],
    enabled: (ctx) => (typeof ctx.keymap.importRpeHotkeys === 'function' ? true : { key: m.rpeImportUnavailable }),
    async run(ctx) {
      await importRpeHotkeys(ctx);
    },
  },
  {
    id: 'keymap.resetAll',
    title: m.cmdResetShortcuts,
    category: 'Help',
    icon: 'RotateCcw',
    level: 'full',
    aliases: ['default shortcuts', 'restore keys'],
    enabled: (ctx) => (ctx.settings.value.keyboard.overrides.length > 0 ? true : { key: m.kmNothingToReset }),
    async run(ctx) {
      const ok = await ctx.ui.confirm({
        title: { key: m.resetShortcutsTitle },
        body: { key: m.resetShortcutsBody, params: { count: ctx.settings.value.keyboard.overrides.length } },
        confirm: { key: m.resetShortcutsConfirm },
        danger: true,
      });
      if (!ok) return;
      ctx.keymap.setOverrides([]);
      ctx.ui.toast({ key: m.resetShortcutsDone }, { kind: 'success' });
    },
  },
];

/** Default and RPE presets share the Settings shortcut (macOS keeps Cmd+, for the browser: Cmd+; there). */
export const SETTINGS_KEYBINDINGS: readonly KeyBinding[] = [
  { chord: 'Ctrl+Comma', command: 'settings.open', mac: 'Ctrl+Semicolon' },
];

// ---------------------------------------------------------------------------------------------------------------
// Palette (`:` settings)
// ---------------------------------------------------------------------------------------------------------------

const PALETTE_LIMIT = 40;

export function settingsPaletteProvider(services: AppServices): PaletteProvider {
  return {
    prefix: ':',
    search(query) {
      const t = (key: string, params?: Readonly<Record<string, string | number>>): string =>
        services.i18n.t(key, params);
      const terms = searchTerms(query);
      const sectionTitle = (id: PageSectionId): string => {
        const sec = PAGE_SECTIONS.find((x) => x.id === id);
        return sec === undefined ? id : t(sec.title);
      };
      const items: PaletteItem[] = [];
      const rows = settingsRows(services);
      const hasFeatures = rows.some((r) => r.section === 'features');
      for (const sec of PAGE_SECTIONS) {
        if (sec.id === 'features' && !hasFeatures) continue;
        if (!matchesTerms(terms, [t(sec.title), t(sec.description), sec.id])) continue;
        items.push({
          id: `settings:section:${sec.id}`,
          title: { key: m.paletteSection, params: { section: t(sec.title) } },
          detail: { key: sec.description },
          icon: sec.icon,
          run: () => {
            services.nav.goSettings(sec.id);
          },
        });
      }
      if (terms.length > 0) {
        for (const b of BLOCKS) {
          if (!matchesTerms(terms, [t(b.title), t(b.keywords)])) continue;
          items.push({
            id: `settings:block:${b.id}`,
            title: { key: b.title },
            detail: { key: m.paletteIn, params: { section: sectionTitle(b.section) } },
            icon: PAGE_SECTIONS.find((x) => x.id === b.section)?.icon ?? 'Settings',
            run: () => {
              services.nav.goSettings(b.id);
            },
          });
        }
        for (const r of rows) {
          if (!matchesTerms(terms, rowSearchTexts(t, r))) continue;
          items.push({
            id: `settings:row:${r.id}`,
            title: { key: r.title },
            detail: { key: m.paletteIn, params: { section: sectionTitle(r.section) } },
            icon: 'SlidersHorizontal',
            run: () => {
              services.nav.goSettings(r.id);
            },
          });
          if (items.length >= PALETTE_LIMIT) break;
        }
      }
      return items.slice(0, PALETTE_LIMIT);
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Help topics
// ---------------------------------------------------------------------------------------------------------------

const HELP_TOPICS: readonly HelpTopicDef[] = [
  { id: 'settings.page', title: m.helpSettingsTitle, body: m.helpSettingsBody, level: 'guided' },
  { id: 'settings.shortcuts', title: m.helpShortcutsTitle, body: m.helpShortcutsBody, level: 'guided' },
];

// ---------------------------------------------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------------------------------------------

/** Keeps the i18n runtime on the language setting (the first-run question, other tabs and this page change it). */
export function syncLocale(services: Pick<AppServices, 'settings' | 'i18n'>): () => void {
  const apply = (s: UserSettings): void => {
    if (services.i18n.locale !== s.general.locale) services.i18n.setLocale(s.general.locale);
  };
  apply(services.settings.value);
  return services.settings.subscribe(apply);
}

const settingsModule = {
  id: 'settings',
  commands: SETTINGS_COMMANDS,
  keybindings: { default: SETTINGS_KEYBINDINGS, rpe: SETTINGS_KEYBINDINGS },
  dialogs: [{ id: DIALOG.keymapRecorder, kind: 'modal', component: KeyRecorderDialog }],
  routes: [
    { path: '#/settings', component: SettingsRoute },
    { path: '#/settings/:section', component: SettingsRoute },
  ],
  paletteProviders: (s) => [settingsPaletteProvider(s)],
  helpTopics: HELP_TOPICS,
  init: (s) => syncLocale(s),
} satisfies FeatureModule;

export default settingsModule;
